<script setup lang="ts">
import RlDisplayPdf from '@/components/common/upload/RlFileViewerPdf.vue'
import RlUpload from '@/components/common/upload/RlUpload.vue'
import { type UploadFile } from 'element-plus'
import { computed, ref } from 'vue'

const props = withDefaults(defineProps<{ maxSizeMb?: number; fileUrl?: string; disabled?: boolean }>(), {
  maxSizeMb: 10,
  fileUrl: undefined,
  disabled: false,
})

// Visualize the file from the URL? This is used to prevent rendering the url file (after the clear button is clicked).
const visualizeUrl = ref(true)

// File uploaded, with metadata (name, etc.) besides content. Null signals deletion.
const uploadFile = defineModel<UploadFile | undefined | null>({ required: true })

const uploadRef = ref<InstanceType<typeof RlUpload>>()

const pdfFileLoaded = computed(() => uploadFile.value?.raw?.type === 'application/pdf')

const isPdfUrl = computed(() => props.fileUrl?.split('?')[0].endsWith('.pdf'))

/** `true` if there's a url set and no file is being uploaded (i.e. file will take precedence). */
const displayPdf = computed(() => !pdfFileLoaded.value && isPdfUrl.value && visualizeUrl.value)

/** Explicit clear by clicking the 'remove' icon in the UI, clear component and set model to `null` */
const flagForRemoval = () => {
  clear()
  uploadFile.value = null
}

const clear = async () => {
  uploadRef.value?.clear()
  uploadFile.value = undefined
  visualizeUrl.value = false
}

const reset = () => {
  clear()
  visualizeUrl.value = true
}

defineExpose({ reset, clear })
</script>

<template>
  <div class="w-full">
    <!-- File uploader -->
    <RlUpload
      v-if="!uploadFile && (!fileUrl || (fileUrl && !visualizeUrl))"
      ref="uploadRef"
      v-model="uploadFile"
      :acceptable-mime-types="['application/pdf']"
      :disabled="disabled"
    >
      <p><span style="color: var(--text-brand)">Upload a file</span> or drag and drop</p>
      <small>PDFs up to {{ maxSizeMb }}MB.</small>
    </RlUpload>

    <!-- PDF viewer -->

    <!-- By URL (takes precedence) -->
    <RlDisplayPdf v-if="isPdfUrl && displayPdf" :url="fileUrl" :disabled="disabled" @clear="flagForRemoval" />

    <!-- By File -->
    <RlDisplayPdf
      v-else-if="pdfFileLoaded && !displayPdf"
      :upload-file="uploadFile!"
      :disabled="disabled"
      @clear="flagForRemoval"
    />
  </div>
</template>

<style scoped lang="scss">
:deep(.el-upload-dragger) {
  height: 140px;
  width: 100%;
  padding: 0;
  line-height: 1.3em;

  .el-icon {
    margin: 0;
    width: 56px;
  }
}
</style>
